import { sha256 } from "@noble/hashes/sha2.js";
import { bytesToHex } from "@noble/hashes/utils.js";
import { z } from "zod";
import { authoringSlugSchema } from "./authoring-shared.js";
import { canonicalTwinBoundaryJson, twinLogicScopeSchema } from "./twin-logic.js";
import { twinLogicProductInventoryResponseSchema } from "./twin-logic-api.js";
import { twinAuthoringPreparationSourceSelectionSchema } from "./twin-authoring-preparations.js";
/** Shared with activation: a preview must use exactly the serving term rule. */
export function normalizedTwinRoutingTerms(terms) {
    return new Set(terms.map((term) => term.trim().toLowerCase().replace(/\s+/gu, " "))
        .filter(Boolean));
}
const text = z.string().trim().min(1).max(4_000);
const terms = z.array(z.string().trim().min(1).max(240)).max(96);
const relation = z.string().regex(/^(connector_warehouse|mlx_curated)\.[a-z_][a-z0-9_]{0,126}$/u);
const unique = (values) => new Set(values).size === values.length;
export const twinAuthoringIntentSchema = z.object({
    productSlug: authoringSlugSchema,
    disposition: z.enum(["extend", "replace", "coexist"]),
    replaces: z.array(authoringSlugSchema).max(64).refine(unique),
    businessQuestions: z.array(text).min(1).max(32),
    sourceRelations: z.array(relation).min(1).max(128).refine(unique),
    routingTerms: terms,
}).strict().superRefine((intent, context) => {
    if ((intent.disposition === "replace") !== (intent.replaces.length > 0)) {
        context.addIssue({ code: "custom", path: ["replaces"],
            message: "Only replace intent names predecessors, and it must name at least one." });
    }
    if (intent.replaces.includes(intent.productSlug)) {
        context.addIssue({ code: "custom", path: ["replaces"],
            message: "Use extend to revise the same product slug." });
    }
});
export const twinAuthoringPreflightInputSchema = z.object({
    scope: twinLogicScopeSchema,
    sourceSelection: twinAuthoringPreparationSourceSelectionSchema,
    intents: z.array(twinAuthoringIntentSchema).max(128),
    expectedCatalogueSha256: z.string().regex(/^[0-9a-f]{64}$/u).optional(),
}).strict().superRefine((input, context) => {
    if (!unique(input.intents.map((intent) => intent.productSlug))) {
        context.addIssue({ code: "custom", path: ["intents"], message: "Output slugs must be unique." });
    }
});
export function twinAuthoringIntentGuidance(rawInput) {
    const plan = twinAuthoringPreflightInputSchema.parse(rawInput);
    if (!plan.expectedCatalogueSha256)
        throw new Error("Resolved intent requires a catalogue hash.");
    return "\n\nResolved operator intent (metadata, not source evidence):\n"
        + canonicalTwinBoundaryJson(plan)
        + "\nEmit every planned public output with exactly these sources, supersedes slugs and routing terms. "
        + "Do not add other public outputs or replace unrelated products. Correctness and evaluation checks still apply.";
}
/** Metadata only: no SQL, source rows, evaluation answers or old view definitions. */
export function preflightTwinAuthoring(inventory, rawInput) {
    const input = twinAuthoringPreflightInputSchema.parse(rawInput);
    const parsed = twinLogicProductInventoryResponseSchema.parse(inventory);
    const catalogue = [];
    for (const item of parsed.items) {
        if (item.product.tenantSlug !== input.scope.tenantSlug
            || item.product.organisationId !== input.scope.organisationId) {
            throw new Error("Twin authoring preflight inventory scope mismatch.");
        }
        if (!item.active)
            continue;
        catalogue.push({
            productSlug: item.product.productSlug,
            productVersionId: item.productVersionId,
            productSha256: item.productSha256,
            activationEventId: item.active.activationEventId,
            capabilityKey: item.product.lifecycle?.capabilityKey ?? null,
            description: item.product.routingCard.description,
            sourceRelations: item.product.relationBinding.sourceRelations
                .map(({ schema, relation: name }) => `${schema}.${name}`).sort(),
            routingTerms: [...normalizedTwinRoutingTerms([
                    ...item.product.routingCard.aliases, ...item.product.routingCard.activationPhrases,
                ])].sort(),
        });
    }
    catalogue.sort((a, b) => a.productSlug < b.productSlug ? -1 : a.productSlug > b.productSlug ? 1 : 0);
    if (!unique(catalogue.map((entry) => entry.productSlug))) {
        throw new Error("Twin authoring preflight inventory has multiple active versions of one product.");
    }
    const catalogueSha256 = bytesToHex(sha256(canonicalTwinBoundaryJson({
        scope: input.scope, catalogue,
    })));
    const issues = [];
    const issue = (code, productSlug, otherProductSlug = null, shared = []) => {
        issues.push({ code, productSlug, otherProductSlug, terms: shared });
    };
    if (input.expectedCatalogueSha256 && input.expectedCatalogueSha256 !== catalogueSha256) {
        issue("catalogue_changed", null);
    }
    const active = new Map(catalogue.map((entry) => [entry.productSlug, entry]));
    const outputs = new Set(input.intents.map((intent) => intent.productSlug));
    const predecessors = new Map();
    const selected = input.sourceSelection.mode === "selected"
        ? new Set(input.sourceSelection.relations) : null;
    for (const intent of input.intents) {
        if (intent.disposition === "extend" && !active.has(intent.productSlug)) {
            issue("extend_requires_active_product", intent.productSlug);
        }
        if (intent.disposition !== "extend" && active.has(intent.productSlug)) {
            issue("output_already_exists", intent.productSlug);
        }
        if (selected && intent.sourceRelations.some((source) => !selected.has(source))) {
            issue("source_outside_selection", intent.productSlug);
        }
        for (const predecessor of intent.replaces) {
            if (!active.has(predecessor))
                issue("unknown_predecessor", intent.productSlug, predecessor);
            if (outputs.has(predecessor))
                issue("output_is_also_predecessor", intent.productSlug, predecessor);
            if (predecessors.has(predecessor))
                issue("predecessor_claimed_twice", intent.productSlug, predecessor);
            predecessors.set(predecessor, intent.productSlug);
        }
        const claimed = normalizedTwinRoutingTerms(intent.routingTerms);
        // Compare against every active product, including ones with unrelated sources.
        // Each product must also be independently activatable before its siblings.
        for (const existing of catalogue) {
            if (existing.productSlug === intent.productSlug || intent.replaces.includes(existing.productSlug))
                continue;
            const shared = existing.routingTerms.filter((term) => claimed.has(term));
            if (shared.length)
                issue("routing_conflict", intent.productSlug, existing.productSlug, shared);
        }
    }
    for (let i = 0; i < input.intents.length; i++) {
        const left = input.intents[i];
        const claimed = normalizedTwinRoutingTerms(left.routingTerms);
        for (const right of input.intents.slice(i + 1)) {
            const shared = [...normalizedTwinRoutingTerms(right.routingTerms)]
                .filter((term) => claimed.has(term)).sort();
            if (shared.length)
                issue("routing_conflict", left.productSlug, right.productSlug, shared);
        }
    }
    return {
        schemaVersion: "mlx.twin-authoring.preflight.v1",
        scope: input.scope,
        catalogueSha256,
        catalogue,
        relatedProductSlugs: catalogue.filter((entry) => !selected
            || entry.sourceRelations.some((source) => selected.has(source)))
            .map((entry) => entry.productSlug),
        intents: input.intents,
        ready: input.intents.length > 0 && issues.length === 0,
        issues,
    };
}
/** The slug the compiler gives the source-record carrier of one relation. */
function compilerSourceRecordCarrierSlug(relationName) {
    // Python twin-authoring hashes canonical JSON with one terminal LF.
    const digest = bytesToHex(sha256(`${canonicalTwinBoundaryJson(`authoring_source.${relationName}`)}\n`));
    return `source-records-${digest.slice(0, 20)}`;
}
/**
 * The compiler's own source-record carrier for one selected relation. Its slug is the digest of
 * that relation's logical name, so an operator cannot plan it and an author cannot take the name;
 * it claims no routing terms and supersedes nothing, so it takes no question from another product.
 */
function isCompilerSourceRecordCarrier(product, selected) {
    const [source, ...others] = product.relationBinding.sourceRelations;
    if (!source || others.length || (selected && !selected.has(`${source.schema}.${source.relation}`)))
        return false;
    const slug = compilerSourceRecordCarrierSlug(source.relation);
    return product.productSlug === slug && product.lifecycle?.capabilityKey === slug
        && product.lifecycle.supersedesProductSlugs.length === 0
        && normalizedTwinRoutingTerms([...product.routingCard.aliases, ...product.routingCard.activationPhrases]).size === 0;
}
/**
 * Carriers of the baseline release that a refinement of selected views reads and leaves out.
 * The build renews the carrier of every selected relation that declares measures. A baseline
 * product outside the selected views cannot be renewed, so each draft is refused as claiming a
 * product outside that scope, whatever the author writes: select the carrier with its readers.
 */
export function unselectedTwinAuthoringBaselineCarriers(inventory, rawInput, baselineReleaseId, selectedBaselineViewNames) {
    const input = twinAuthoringPreflightInputSchema.parse(rawInput);
    const family = new Map(twinLogicProductInventoryResponseSchema.parse(inventory).items
        .filter((item) => item.product.source.verifiedAuthoringReleaseId === baselineReleaseId)
        .map((item) => [item.product.productSlug, item.product]));
    // The build takes each selected view with its transitive parents.
    const scope = new Set();
    const pending = selectedBaselineViewNames.map((name) => name.replaceAll("_", "-"));
    for (let slug = pending.pop(); slug !== undefined; slug = pending.pop()) {
        if (scope.has(slug))
            continue;
        scope.add(slug);
        pending.push(...family.get(slug)?.lifecycle?.dependencyProductSlugs ?? []);
    }
    const selected = input.sourceSelection.mode === "selected" ? new Set(input.sourceSelection.relations) : null;
    return [...family.values()].filter((product) => !scope.has(product.productSlug)
        && product.relationBinding.sourceRelations.some(({ schema, relation: name }) => {
            if (selected && !selected.has(`${schema}.${name}`))
                return false;
            const slug = compilerSourceRecordCarrierSlug(name);
            // The build compares a renewed carrier with a baseline product by name and by capability.
            return product.productSlug === slug || product.lifecycle?.capabilityKey === slug;
        })).map((product) => product.productSlug).sort();
}
/** Check admitted output metadata before an operator accepts its definition proposal. */
export function verifyTwinAuthoringPlan(inventory, rawInput, releaseId) {
    if (!/^ta_rel_[0-9a-f]{32}$/u.test(releaseId))
        throw new Error("Invalid accepted release ID.");
    const input = twinAuthoringPreflightInputSchema.parse(rawInput);
    if (!input.expectedCatalogueSha256)
        throw new Error("Plan verification requires the inspected catalogue hash.");
    const preflight = preflightTwinAuthoring(inventory, input);
    if (!preflight.ready)
        throw new Error("Resolve the current preflight issues before verifying outputs.");
    const inventoryItems = twinLogicProductInventoryResponseSchema.parse(inventory).items;
    const items = inventoryItems.filter((item) => item.product.source.verifiedAuthoringReleaseId === releaseId);
    const failures = [];
    const planned = new Set(input.intents.map((intent) => intent.productSlug));
    const selected = input.sourceSelection.mode === "selected" ? new Set(input.sourceSelection.relations) : null;
    const compilerCarriers = [];
    // The build rebuilds each planned output with its transitive parents.
    const released = new Map(items.map((item) => [item.product.productSlug, item.product]));
    const parents = new Set();
    const pending = [...planned];
    for (let slug = pending.pop(); slug !== undefined; slug = pending.pop()) {
        for (const parent of released.get(slug)?.lifecycle?.dependencyProductSlugs ?? []) {
            if (!parents.has(parent))
                pending.push(parent);
            parents.add(parent);
        }
    }
    const servingVisibility = new Map(inventoryItems.filter((item) => item.active)
        .map((item) => [item.product.productSlug, item.product.lifecycle?.catalogVisibility]));
    const supportingParents = [];
    for (const item of items) {
        // A release gains or renews a carrier without the operator asking; it is not a planned output.
        if (!planned.has(item.product.productSlug) && isCompilerSourceRecordCarrier(item.product, selected)) {
            compilerCarriers.push(item.product.productSlug);
            continue;
        }
        // A supporting parent cannot be planned (a planned output is public) and is no public claim:
        // it stays supporting, supersedes nothing and is only loaded with the output built on it.
        if (!planned.has(item.product.productSlug) && parents.has(item.product.productSlug)
            && item.product.lifecycle?.catalogVisibility === "supporting"
            && item.product.lifecycle.supersedesProductSlugs.length === 0
            && (servingVisibility.get(item.product.productSlug) ?? "supporting") === "supporting") {
            supportingParents.push(item.product.productSlug);
            continue;
        }
        if (item.product.lifecycle?.catalogVisibility !== "supporting" && !planned.has(item.product.productSlug)) {
            failures.push(`unplanned_public_output:${item.product.productSlug}`);
        }
        if (!planned.has(item.product.productSlug)
            && (item.product.lifecycle?.supersedesProductSlugs.length
                || preflight.catalogue.some((entry) => entry.productSlug === item.product.productSlug))) {
            failures.push(`unplanned_replacement:${item.product.productSlug}`);
        }
    }
    const same = (left, right) => JSON.stringify([...left].sort()) === JSON.stringify([...right].sort());
    for (const intent of input.intents) {
        const matches = items.filter((item) => item.product.productSlug === intent.productSlug);
        if (matches.length !== 1) {
            failures.push(`expected_one_output:${intent.productSlug}`);
            continue;
        }
        const product = matches[0].product;
        if (product.lifecycle?.catalogVisibility !== "public")
            failures.push(`output_not_public:${intent.productSlug}`);
        if (!same(product.lifecycle?.supersedesProductSlugs ?? [], intent.replaces)) {
            failures.push(`replacement_differs:${intent.productSlug}`);
        }
        if (!same([...normalizedTwinRoutingTerms([
                ...product.routingCard.aliases, ...product.routingCard.activationPhrases,
            ])], [...normalizedTwinRoutingTerms(intent.routingTerms)]))
            failures.push(`routing_differs:${intent.productSlug}`);
        if (!same(product.relationBinding.sourceRelations.map(({ schema, relation: name }) => `${schema}.${name}`), intent.sourceRelations))
            failures.push(`sources_differ:${intent.productSlug}`);
        const predecessor = preflight.catalogue.find((entry) => entry.productSlug === intent.productSlug);
        if (intent.disposition === "extend" && predecessor?.capabilityKey
            && product.lifecycle?.capabilityKey !== predecessor.capabilityKey) {
            failures.push(`capability_differs:${intent.productSlug}`);
        }
    }
    return { releaseId, catalogueSha256: preflight.catalogueSha256,
        verified: failures.length === 0, failures, compilerCarriers: compilerCarriers.sort(),
        supportingParents: supportingParents.sort(),
        productVersions: items.map(({ productVersionId, productSha256 }) => ({ productVersionId, productSha256 })) };
}
